import { Service } from '@angular/core';

/**
 * One attached route-backed drawer form and the two questions the router needs
 * to answer about it. Produced by `DrawerRouteBridge.attach` from the sheet's
 * own `dirty` signal; consumed by `drawerDirtyGuard` (and the bridge's
 * `zBeforeClose` hook) through {@link DrawerFormVeto}.
 */
export interface DrawerFormVetoEntry {
  /** Is the attached form dirty right now? A signal read, not a snapshot. */
  readonly isDirty: () => boolean;
  /** Ask the user; `true` = discard the draft and let the close/navigation win. */
  readonly confirmDiscard: () => boolean;
}

/**
 * The root registry of dirty drawer forms, keyed by `sectionKey` (the section
 * route's own URL segments — derived, never configured).
 *
 * Root-scoped on purpose (`DESIGN-WUIAM-135` §3.5): route guards run in the
 * router/environment injector, NOT the component's, so the guard and the
 * component-provided `DrawerRouteBridge` can only meet in a root singleton.
 * The bridge registers on sheet attach and unregisters on close, so at most one
 * entry per section exists and a route change after a close sees a clean
 * section (`isDirty` false → the guard allows). Absence is always clean: a
 * section with no route-backed form open can never block a navigation.
 */
@Service()
export class DrawerFormVeto {
  private readonly entries = new Map<string, DrawerFormVetoEntry>();

  /** Replaces any previous entry for the key (one drawer form per section). */
  register(key: string, entry: DrawerFormVetoEntry): void {
    this.entries.set(key, entry);
  }

  unregister(key: string): void {
    this.entries.delete(key);
  }

  /** `false` when nothing is registered under the key. */
  isDirty(key: string): boolean {
    return this.entries.get(key)?.isDirty() ?? false;
  }

  /** `true` (allow) when nothing is registered — absence is never a veto. */
  confirmDiscard(key: string): boolean {
    return this.entries.get(key)?.confirmDiscard() ?? true;
  }
}
